import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { axiosInstance } from '../../helpers'
import { Lead } from '../../models/Outreach/types'
import { TenantMessage } from '../../models/TenantMessage'

export type LeadNoteSource = 'staff' | 'tour' | 'call'

export interface LeadNote {
    id: number | null
    body: string
    created_by: number | null
    created_by_name: string
    created_date: string | null
    source?: LeadNoteSource
    source_id?: number
}

export interface LeadAuditEvent {
    at: string | null
    kind: string
    title: string
    detail: string
    source: string
    payload: Record<string, unknown>
}

export const useLeadNotes = (leadId: number | null) => {
    const queryClient = useQueryClient()
    const query = useQuery<LeadNote[], Error>({
        queryKey: ['lead-notes', leadId],
        queryFn: async () => {
            const res = await axiosInstance.get<LeadNote[]>(
                `outreach/leads/${leadId}/notes/`,
            )
            return res.data
        },
        enabled: leadId != null,
        refetchOnWindowFocus: false,
    })

    const addNote = useMutation({
        mutationFn: async (body: string) => {
            const res = await axiosInstance.post<LeadNote>(
                `outreach/leads/${leadId}/notes/`,
                { body },
            )
            return res.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['lead-notes', leadId] })
            queryClient.invalidateQueries({
                queryKey: ['lead-audit-trail', leadId],
            })
            toast.success('Note saved')
        },
        onError: () => {
            toast.error('Unable to save note')
        },
    })

    return {
        notes: query.data ?? [],
        isLoading: query.isLoading,
        addNote: addNote.mutateAsync,
        isSaving: addNote.isLoading,
    }
}

export const useLeadAuditTrail = (leadId: number | null, enabled: boolean) => {
    const query = useQuery<LeadAuditEvent[], Error>({
        queryKey: ['lead-audit-trail', leadId],
        queryFn: async () => {
            const res = await axiosInstance.get<{ results: LeadAuditEvent[] }>(
                `outreach/leads/${leadId}/audit-trail/`,
            )
            return res.data.results ?? []
        },
        enabled: leadId != null && enabled,
        refetchOnWindowFocus: false,
    })

    return {
        events: query.data ?? [],
        isLoading: query.isLoading,
        refetch: query.refetch,
    }
}

export const useSendLeadMessage = (leadId: number | null) => {
    const queryClient = useQueryClient()
    const mutation = useMutation({
        mutationFn: async (input: { subject: string; body: string }) => {
            const res = await axiosInstance.post<TenantMessage>(
                `outreach/leads/${leadId}/send-message/`,
                input,
            )
            return res.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tenant-messages'] })
            queryClient.invalidateQueries({
                queryKey: ['lead-audit-trail', leadId],
            })
            toast.success(
                'Message sent. They’ll get an email to open the portal.',
            )
        },
        onError: () => {
            toast.error('Unable to send message')
        },
    })

    return {
        sendMessage: mutation.mutateAsync,
        isSending: mutation.isLoading,
    }
}

export interface LeadPreferencesInput {
    preferred_unit_config_id?: number | null
    preferred_rent_cost?: number | null
    preferred_move_in_date?: string | null
    preferred_move_out_date?: string | null
    school_attendance_id?: number | null
    additional_requests?: string
    preferred_premium_ids?: number[]
    roommate_names?: string[]
}

export interface LeadPetInput {
    id?: number
    name: string
    animal_type: string
    breed?: string
    weight?: string | null
    notes?: string
}

const invalidatePersonFile = (
    queryClient: ReturnType<typeof useQueryClient>,
    leadId: number | null,
) => {
    queryClient.invalidateQueries({ queryKey: ['person-file-lead', leadId] })
    queryClient.invalidateQueries({ queryKey: ['leads'], exact: false })
    queryClient.invalidateQueries({
        queryKey: ['lead-audit-trail', leadId],
    })
}

export const useUpdateLeadPreferences = (leadId: number | null) => {
    const queryClient = useQueryClient()
    const mutation = useMutation({
        mutationFn: async (input: LeadPreferencesInput) => {
            const res = await axiosInstance.patch<Lead>(
                `outreach/leads/${leadId}/preferences/`,
                input,
            )
            return res.data
        },
        onSuccess: (lead) => {
            if (leadId != null) {
                queryClient.setQueryData(['person-file-lead', leadId], lead)
            }
            invalidatePersonFile(queryClient, leadId)
            toast.success('Preferences saved')
        },
        onError: () => {
            toast.error('Unable to save preferences')
        },
    })
    return {
        updatePreferences: mutation.mutateAsync,
        isSaving: mutation.isLoading,
    }
}

export const useReplaceLeadPets = (leadId: number | null) => {
    const queryClient = useQueryClient()
    const mutation = useMutation({
        mutationFn: async (pets: LeadPetInput[]) => {
            const res = await axiosInstance.put<Lead>(
                `outreach/leads/${leadId}/pets/`,
                { pets },
            )
            return res.data
        },
        onSuccess: (lead) => {
            if (leadId != null) {
                queryClient.setQueryData(['person-file-lead', leadId], lead)
            }
            invalidatePersonFile(queryClient, leadId)
            toast.success('Pets saved')
        },
        onError: () => {
            toast.error('Unable to save pets')
        },
    })
    return {
        replacePets: mutation.mutateAsync,
        isSaving: mutation.isLoading,
    }
}

export const useDeleteLead = () => {
    const queryClient = useQueryClient()
    const mutation = useMutation({
        mutationFn: async (leadId: number) => {
            await axiosInstance.delete(`/outreach/leads/${leadId}/`)
        },
        onSuccess: (_data, leadId) => {
            queryClient.removeQueries({
                queryKey: ['person-file-lead', leadId],
            })
            queryClient.invalidateQueries({ queryKey: ['leads'], exact: false })
            queryClient.invalidateQueries({
                queryKey: ['leasing-season-pipeline-counts'],
                exact: false,
            })
            toast.success('Lead deleted')
        },
        onError: () => {
            toast.error('Unable to delete lead')
        },
    })
    return {
        deleteLead: mutation.mutateAsync,
        isDeleting: mutation.isLoading,
    }
}
